import { useEffect, useRef, useState } from "react";
import HeaderTools from "../../components/HeaderTools";
import {
  Badge,
  Box,
  Button,
  CloseButton,
  Flex,
  Image,
  Spinner,
  Stack,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FiCamera, FiStar } from "react-icons/fi";
import api from "../../services/api";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { CiTrash } from "react-icons/ci";

const normalizeImageUrl = (path) => {
  if (!path) return "";

  const normalized = path.replace(/\\/g, "/");

  const index = normalized.indexOf("/uploads");
  if (index === -1) return "";

  return `https://api.aqvex.com.br${normalized.substring(index)}`;
};

export default function ProductImages() {
  const [selectedColor, setSelectedColor] = useState("geral");
  const [showNotification, setShowNotification] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const [productVariations, setProductVariations] = useState([]);
  const [productInformation, setProductInformation] = useState(null);
  const [isLoadingProduct, setIsLoadingProduct] = useState(true);
  const [isLoadingVariations, setIsLoadingVariations] = useState(true);
  const [productImages, setProductImages] = useState([]);

  const { id } = useParams();

  const getProductImagens = async () => {
    try {
      const response = await api.get(`/admin/products/${id}/colors-images`);

      setProductImages(response.data.data);
    } catch {
    } finally {
      setIsLoadingVariations(false);
    }
  };

  const uploadImages = async () => {
    try {
      for (const item of uploadedFiles) {
        const formData = new FormData();

        formData.append("images", item.file);
        formData.append("colors", item.color);
        formData.append("principals", item.isPrimary ? "S" : "N");

        await api.post(`/admin/products/${id}/images`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      toast.success("Imagens enviadas com sucesso!");
      await getProductImagens();
      setUploadedFiles([]);
    } catch (error) {
      toast.error("Erro ao enviar as imagens.");
    }
  };

  const deleteImage = async (imageId) => {
    try {
      const response = await api.delete(
        `/admin/products/${id}/images/${imageId}`,
      );
      await getProductImagens();
      toast.success("Imagem deletada com sucesso!");
    } catch {
      toast.error("Erro ao deletar a imagem.");
    } finally {
    }
  };

  const setSavedImageAsPrimary = async (imageId) => {
    try {
      await api.patch(`/admin/products/${id}/images/${imageId}/principal`);
      await getProductImagens();
      toast.success("Foto principal alterada com sucesso!");
    } catch {
      toast.error("Erro ao alterar a foto principal.");
    }
  };

  useEffect(() => {
    const getProductVariations = async () => {
      try {
        const response = await api.get(`/admin/products/${id}/variations`);
        setProductVariations(response.data.data);
      } catch {
      } finally {
        setIsLoadingVariations(false);
      }
    };

    const getProductInformation = async () => {
      try {
        const response = await api.get(`/admin/products/${id}`);
        setProductInformation(response.data.data[0]);
      } catch {
      } finally {
        setIsLoadingProduct(false);
      }
    };

    getProductImagens();
    getProductInformation();
    getProductVariations();
  }, []);

  const handleColorChange = (colorId) => {
    setSelectedColor(colorId);
  };

  const handleFileUpload = (files) => {
    const validFiles = Array.from(files).filter((file) => {
      const validTypes = [
        "image/png",
        "image/jpg",
        "image/jpeg",
        "image/gif",
        "image/webp",
      ];

      const maxSize = 5 * 1024 * 1024;

      if (file.size > maxSize) {
        toast.error(`O arquivo ${file.name} excede o tamanho máximo de 5MB.`);
        return false;
      }

      return validTypes.includes(file.type);
    });

    if (validFiles.length > 0) {
      setUploadedFiles((prev) => [
        ...prev,
        ...validFiles.map((file) => ({
          file,
          color: selectedColor,
          isPrimary: false,
        })),
      ]);
    }
  };

  const setPrimaryImage = (index) => {
    setUploadedFiles((prev) =>
      prev.map((item, i) => ({
        ...item,
        isPrimary: i === index, // 🔥 só uma verdadeira
      })),
    );
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    handleFileUpload(files);
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files) {
      handleFileUpload(files);
    }
  };

  const handleVideoUpload = () => {};

  const removeFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const sizes = ["P", "M", "G", "GG"];

  const hasPrimaryImageForColor = (color) => {
    return productImages.some(
      (img) => img.cor_variacao === color && img.principal?.includes("S"),
    );
  };

  const getColorThumbnail = (color) => {
    const principal = productImages.find(
      (img) => img.cor_variacao === color && img.principal?.includes("S"),
    );
    const first = productImages.find((img) => img.cor_variacao === color);
    return normalizeImageUrl((principal || first)?.url_imagem);
  };

  return (
    <>
      <HeaderTools
        title={
          isLoadingProduct ? (
            <Spinner size="sm" />
          ) : (
            (productInformation?.prod_descricao ?? "-")
          )
        }
        backTo="/painel/produtos"
      />
      <Box
        padding={{ base: "16px", md: "20px" }}
        bg="gray.50"
        minHeight="100vh"
        gap="20px"
        display="flex"
        flexDirection="column"
      >
        <Flex width="100%" justifyContent="space-between" alignItems="center">
          <Flex flexDirection="column">
            {isLoadingProduct ? (
              <Spinner size="sm" />
            ) : (
              <>
                <Text>{productInformation?.prod_descricao}</Text>
                <Text color="gray.400">
                  #{productInformation?.prod_referencia}
                </Text>
              </>
            )}
          </Flex>

          <Text fontWeight={600}>
            {isLoadingProduct ? (
              <Spinner size="sm" />
            ) : (
              new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(productInformation?.preco)
            )}
          </Text>
        </Flex>

        <Box>
          <Text
            fontSize={{ base: "18px", md: "20px" }}
            fontWeight="bold"
            color="gray.800"
            mb={4}
          >
            Fotos
          </Text>
          {showNotification && (
            <Box
              bg="cyan.50"
              borderColor="cyan.200"
              borderWidth="1px"
              borderRadius="8px"
              mb={6}
              py={3}
              px={4}
              position="relative"
            >
              <Box flex="1">
                <Text fontSize={{ base: "14px", md: "15px" }} color="cyan.700">
                  <strong>NOVIDADE!</strong> Agora você pode inserir fotos de
                  acordo com as cores
                </Text>
              </Box>
              <CloseButton
                size="sm"
                onClick={() => setShowNotification(false)}
                color="cyan.600"
                position="absolute"
                top="8px"
                right="8px"
              />
            </Box>
          )}
          {isLoadingVariations ? (
            <Spinner />
          ) : (
            <Stack
              direction={{ base: "row", sm: "row" }}
              spacing={{ base: 4, md: 6 }}
              mb={6}
              flexWrap="no-wrap"
              gap={5}
              overflow="auto"
            >
              {productVariations.map((filter) => {
                const isSelected = selectedColor === filter.cor_variacao;
                const hasPrimary = hasPrimaryImageForColor(filter.cor_variacao);
                const thumbnail = getColorThumbnail(filter.cor_variacao);

                return (
                  <Flex
                    key={filter.cor_variacao}
                    direction="column"
                    align="center"
                    cursor="pointer"
                    onClick={() => handleColorChange(filter.cor_variacao)}
                    gap={2}
                    position="relative"
                  >
                    {hasPrimary && (
                      <Badge
                        position="absolute"
                        top="-6px"
                        fontSize="10px"
                        colorScheme="green"
                        borderRadius="full"
                        px={2}
                        zIndex={2}
                      >
                        Principal
                      </Badge>
                    )}
                    <Box
                      w={{ base: "36px", md: "40px" }}
                      h={{ base: "36px", md: "40px" }}
                      borderRadius="50%"
                      borderWidth="2px"
                      borderStyle="solid"
                      borderColor={isSelected ? "#38A169" : "#CBD5E0"}
                      boxShadow={
                        isSelected ? "0 0 0 2px rgba(56,161,105,0.2)" : "none"
                      }
                      overflow="hidden"
                    >
                      <Box
                        w="100%"
                        h="100%"
                        borderRadius="50%"
                        bg={thumbnail ? undefined : (filter.cor_hexadecimal || "#FFFFFF")}
                        bgImage={thumbnail ? `url(${thumbnail})` : undefined}
                        bgSize="cover"
                        bgPosition="center"
                        bgRepeat="no-repeat"
                      />
                    </Box>
                    <Text
                      fontSize={{ base: "11px", md: "12px" }}
                      color="#4A4A4A"
                      textAlign="center"
                    >
                      {filter.cor_variacao}
                    </Text>
                  </Flex>
                );
              })}
            </Stack>
          )}
          <Flex width="100%" overflow="auto" gap="10px" mb={10}>
            {productImages
              .filter(({ cor_variacao }) => cor_variacao === selectedColor)
              .map((image) => (
                <Box
                  w="120px"
                  height="120px"
                  position="relative"
                  key={image.id}
                >
                  <Box
                    position="absolute"
                    zIndex={1}
                    backgroundColor="red"
                    padding="5px"
                    borderRadius="8px"
                    cursor="pointer"
                    onClick={() => deleteImage(image.id)}
                  >
                    <CiTrash size={22} color="white" />
                  </Box>

                  {image.principal?.includes("S") ? (
                    <Badge position="absolute" px="15px" right={0} zIndex={1}>
                      Principal
                    </Badge>
                  ) : (
                    <Box
                      position="absolute"
                      zIndex={1}
                      right={0}
                      backgroundColor="white"
                      padding="5px"
                      borderRadius="8px"
                      cursor="pointer"
                      title="Definir como principal"
                      onClick={() => setSavedImageAsPrimary(image.id)}
                    >
                      <FiStar size={20} color="#38A169" />
                    </Box>
                  )}
                  <Image src={normalizeImageUrl(image.url_imagem)} w="100%" />
                </Box>
              ))}
          </Flex>
          <Box
            border="2px dashed"
            borderColor={isDragOver ? "cyan.400" : "gray.300"}
            borderRadius="12px"
            p={{ base: 8, md: 12 }}
            textAlign="center"
            bg={isDragOver ? "cyan.50" : "white"}
            transition="all 0.2s"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            cursor="pointer"
            onClick={() => fileInputRef.current?.click()}
            minH="300px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <VStack spacing={4}>
              <Box
                w={{ base: "60px", md: "80px" }}
                h={{ base: "60px", md: "80px" }}
                bg="gray.100"
                borderRadius="12px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box fontSize={{ base: "24px", md: "32px" }} color="gray.400">
                  <FiCamera />
                </Box>
              </Box>

              <VStack spacing={2}>
                <Button
                  bg="#8F2CAD"
                  color="white"
                  size={{ base: "md", md: "lg" }}
                  px={6}
                  borderRadius="8px"
                  fontWeight="semibold"
                  _hover={{ bg: "gray.700" }}
                  _active={{ bg: "gray.900" }}
                >
                  Adicionar fotos
                </Button>

                <Text
                  fontSize={{ base: "12px", md: "14px" }}
                  color="gray.500"
                  maxW="400px"
                  lineHeight="1.4"
                >
                  Você pode adicionar até 9 fotos no formato PNG, JPG, JPEG, GIF
                  ou WEBP.
                </Text>

                <Text
                  fontSize={{ base: "11px", md: "12px" }}
                  color="gray.400"
                  lineHeight="1.3"
                >
                  A dimensão recomendada para o upload de foto é de 1000x1250px.
                </Text>

                <Text
                  fontSize={{ base: "11px", md: "12px" }}
                  color="gray.400"
                  lineHeight="1.3"
                >
                  O tamanho recomendado para o upload de foto é de 1MB a 5MB ou
                  5MB.
                </Text>
              </VStack>
            </VStack>
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".png,.jpg,.jpeg,.gif,.webp"
            onChange={handleFileInputChange}
            style={{ display: "none" }}
          />
          {uploadedFiles.length > 0 && (
            <Box mt={6}>
              <Text
                fontSize="16px"
                fontWeight="semibold"
                color="gray.800"
                mb={3}
              >
                Fotos adicionadas ({uploadedFiles.length})
              </Text>
              <Stack spacing={2}>
                {uploadedFiles.map((item, index) => (
                  <Flex
                    key={index}
                    align="center"
                    justify="space-between"
                    bg="white"
                    p={3}
                    borderRadius="8px"
                    border="1px solid"
                    borderColor={item.isPrimary ? "green.300" : "gray.200"}
                  >
                    <Text fontSize="14px" color="gray.700" flex="1" mr={2}>
                      {item.file.name}
                      <Text as="span" color="gray.400" ml={2}>
                        ({item.color})
                      </Text>
                    </Text>

                    <Text fontSize="12px" color="gray.500" mr={3}>
                      {(item.file.size / 1024 / 1024).toFixed(2)} MB
                    </Text>

                    <Button
                      size="sm"
                      variant={item.isPrimary ? "solid" : "outline"}
                      colorScheme="green"
                      mr={2}
                      onClick={() => setPrimaryImage(index)}
                      padding="10px"
                    >
                      {item.isPrimary ? "Principal" : "Definir como principal"}
                    </Button>

                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => removeFile(index)}
                    >
                      ✕
                    </Button>
                  </Flex>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
        {uploadedFiles.length > 0 && (
          <Flex justify="flex-end" mt={6}>
            <Button
              bg="#38A169"
              color="white"
              px={6}
              onClick={uploadImages}
              _hover={{ bg: "green.600" }}
            >
              Enviar fotos
            </Button>
          </Flex>
        )}
      </Box>
    </>
  );
}
